import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { auth, db } from '../config/firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { useDummyData } from './DummyDataContext';
import { useAuth } from './AuthContext';

const CreditsContext = createContext(null);

export function CreditsProvider({ children }) {
  const [credits, setCredits] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const { isDummyDataEnabled } = useDummyData();
  const { user } = useAuth();

  useEffect(() => {
    if (isDummyDataEnabled) {
      setIsLoading(false);
      return;
    }

    let unsubscribe = () => {};

    if (user) {
      const userRef = doc(db, 'users', user.uid);
      
      // Set up real-time listener for credits
      unsubscribe = onSnapshot(userRef, (doc) => {
        if (doc.exists()) {
          setCredits(doc.data()?.credits || 0);
        }
        setIsLoading(false);
      }, (error) => {
        console.error('Error listening to credits:', error);
        setIsLoading(false);
      });
    } else {
      setCredits(0);
      setIsLoading(false);
    }

    return () => unsubscribe();
  }, [user, isDummyDataEnabled]);

  // No updateCredits here on purpose: credit balances are server-side only
  // (firestore.rules rejects client writes to the field). Paid operations
  // charge inside their Cloud Function.
  const checkCredits = useCallback(async (requiredAmount) => {
    if (!user) return false;

    try {
      if (isDummyDataEnabled) {
        return credits >= requiredAmount;
      }

      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      return (userDoc.exists() && (userDoc.data()?.credits || 0) >= requiredAmount);
    } catch (error) {
      console.error('Error checking credits:', error);
      return false;
    }
  }, [user, credits, isDummyDataEnabled]);

  const value = {
    credits,
    isLoading,
    checkCredits
  };

  return (
    <CreditsContext.Provider value={value}>
      {children}
    </CreditsContext.Provider>
  );
}

export function useCredits() {
  const context = useContext(CreditsContext);
  if (!context) {
    throw new Error('useCredits must be used within a CreditsProvider');
  }
  return context;
}